import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import docImg from "../../../assets/icons/document.png";
import BadgeStatus from "../../../common/BadgeStatus";
import { config } from "../../../config/index";
import { newFetch, PUT } from "../../../helpers/fetch";
import AlertService from "../../SweetAlert";
import ToastUtil from "../../ToastNotification/ToastUtil";
const { SERVER_URL } = config;

const LeaveLog = (props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedLeaves, setSelectedLeaves] = useState([]);

  const handleCheckboxChange = (id, isChecked) => {
    if (isChecked) {
      setSelectedLeaves((prev) => [...prev, id]);
    } else {
      setSelectedLeaves((prev) => prev.filter((leaveID) => leaveID !== id));
    }
  };

  const handleBulkCancel = async () => {
    if (selectedLeaves.length === 0) return;
    if (
      await AlertService.confirm(
        "",
        `Are you sure you want to cancel ${selectedLeaves.length} leave(s)?`,
      )
    ) {
      const response = await newFetch(PUT, "/associate-leaves/bulk-cancel", {
        leaveIds: selectedLeaves,
      });

      if (response.success) {
        ToastUtil.success("Leave(s) cancelled successfully");
        setSelectedLeaves([]);
        props.updateState();
      } else {
        ToastUtil.error(response.message || "Failed to cancel leave(s)");
      }
    }
  };

  // Helper function to check if leave is pending or approved
  const isPendingOrApproved = (status) => {
    const lowerStatus = String(status || "").toLowerCase();
    return (
      (lowerStatus.includes("pending") &&
        !lowerStatus.includes("pending cancellation")) ||
      lowerStatus.includes("approved")
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return isNaN(date)
      ? "-"
      : date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
  };

  const formatDateRange = (from, to) => {
    const fromFormatted = formatDate(from);
    const toFormatted = formatDate(to);
    return `${fromFormatted} - ${toFormatted}`;
  };

  return (
    <div className="table-responsive bg-white rounded shadow p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">{t("Submissions")}</h5>
        <button
          className="btn btn-dark"
          disabled={selectedLeaves.length === 0}
          onClick={handleBulkCancel}
        >
          {t("Cancel Leave")}
        </button>
      </div>

      <hr className="my-2" />

      <table className="table table-borderless mb-0">
        <tbody>
          {props.leaves?.length > 0 ? (
            props.leaves.map((leave, index) => (
              <tr key={index} className="align-middle border-bottom">
                {/* Checkbox column */}
                <td className="align-middle">
                  {isPendingOrApproved(leave.status) && (
                    <div className="form-check">
                      <input
                        className="form-check-input position-static"
                        type="checkbox"
                        value={leave.id}
                        checked={selectedLeaves.includes(leave.id)}
                        onChange={(e) =>
                          handleCheckboxChange(leave.id, e.target.checked)
                        }
                      />
                    </div>
                  )}
                </td>

                {/* Content column */}
                <td colSpan="3" className="w-100">
                  {/* Top row: Leave ID & Type + Badge */}
                  <div className="d-flex justify-content-between">
                    <button
                      className="btn btn-link p-0 text-start"
                      onClick={() => {
                        history.push({
                          pathname: "/leave-view",
                          state: { leaveData: leave },
                        });
                      }}
                    >
                      {leave.id} - {t(leave.leave_type_info?.name) || "-"}{" "}
                      {leave?.leave_coverage_name
                        ? `- ${t(leave.leave_coverage_name)}`
                        : ""}
                    </button>

                    <BadgeStatus t={t} status={leave.status} />
                  </div>

                  {/* Bottom row: From - To | Days | Attachment */}
                  <div className="d-flex align-items-center mt-1">
                    {/* Left side: date range and days */}
                    <div className="text-muted small d-flex align-items-center">
                      <div>
                        {formatDateRange(leave.leave_from, leave.leave_to)}
                      </div>
                      <div className="mx-2"></div>
                      <div className="text-start">
                        <b>{leave.no_of_days ?? 0}</b> {t("days")}
                      </div>
                    </div>

                    {/* Spacer pushes attachment icon to right */}
                    <div className="flex-grow-1"></div>

                    {/* Attachment icon aligned right */}
                    {!leave.allow_document && (
                      <div className="d-flex flex-column">
                        {leave.documents &&
                          leave.documents.length > 0 &&
                          leave.documents.map((doc, index) => (
                            <div
                              key={index}
                              className="mb-2 d-flex align-items-center justify-content-between"
                            >
                              <div className="d-flex align-items-center">
                                <a
                                  href={`${SERVER_URL}/storage/leaves/${doc.new_file}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="d-flex align-items-center"
                                >
                                  <img
                                    src={docImg}
                                    alt="Document Icon"
                                    className="me-2"
                                    style={{ width: "32px", height: "32px" }}
                                  />
                                </a>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" className="text-center text-muted py-4">
                {t("No records found.")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default LeaveLog;
